import { useEffect, useRef, useState } from "react";
import { InputOTP } from "@/shared/components/otp-input";
import { useCountdown } from "./useCountdown";
import { AuthSubmitButton } from "@/shared/components/AuthSubmitButton";
import { Button } from "@/shared/components/button";
import {
  OTP_LENGTH,
  OTP_VALIDITY_SECONDS,
  OTP_EXPIRY_WARNING_SECONDS,
} from "./otpConfig";
import { toastError, toastSuccess, toastWarning } from "@/shared/lib/toast";
import { getAuthFlowState, updateAuthFlowState } from "./authFlowState";

// Countdowns are seeded from absolute timestamps persisted in sessionStorage, not
// hardcoded durations — otherwise a page refresh would restart both timers from
// scratch instead of resuming from the real server-side deadline.
function remainingSecondsUntil(timestamp: number | undefined): number | null {
  if (!timestamp) return null;
  return Math.max(0, Math.ceil((timestamp - Date.now()) / 1000));
}

function initialValidity(email: string): { seconds: number; expiresAt: number } {
  const stored = getAuthFlowState();
  if (stored?.email === email && stored.otpExpiresAt) {
    return { seconds: remainingSecondsUntil(stored.otpExpiresAt)!, expiresAt: stored.otpExpiresAt };
  }
  return { seconds: OTP_VALIDITY_SECONDS, expiresAt: Date.now() + OTP_VALIDITY_SECONDS * 1000 };
}

function initialRateLimit(email: string): number {
  const stored = getAuthFlowState();
  if (stored?.email === email) return remainingSecondsUntil(stored.resendBlockedUntil) ?? 0;
  return 0;
}

interface OtpActionResult {
  success: boolean;
  error?: string;
  code?: string;
  retryAfterSeconds?: number;
  // Only populated by the password-reset flow's onVerify (see VerifyResetOtpPage) —
  // ignored by callers (e.g. signup verification) that don't need it.
  resetToken?: string;
  // Only populated by the signup-claim flow's onVerify (see VerifyOtpPage) —
  // ignored by callers (e.g. password reset) that don't need it.
  wasClaim?: boolean;
  creatorName?: string | null;
}

interface OtpPanelProps {
  email: string;
  title: string;
  subtitle: string;
  onVerify: (otp: string) => Promise<OtpActionResult>;
  onResend: () => Promise<OtpActionResult>;
  onVerified: (otp: string, resetToken?: string, wasClaim?: boolean, creatorName?: string | null) => void;
}

function formatTime(seconds: number) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

export function OtpPanel({ email, title, subtitle, onVerify, onResend, onVerified }: OtpPanelProps) {
  const [otp, setOtp] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);

  const [{ seconds: initialValiditySeconds, expiresAt: initialExpiresAt }] = useState(() => initialValidity(email));
  const [initialRateLimitSeconds] = useState(() => initialRateLimit(email));

  const validity = useCountdown(initialValiditySeconds);
  const rateLimitCountdown = useCountdown(initialRateLimitSeconds);

  // Persist the freshly-computed expiry once on mount so it survives the next refresh too.
  useEffect(() => {
    updateAuthFlowState(email, { otpExpiresAt: initialExpiresAt });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const expired = validity.secondsLeft <= 0;
  const showExpiryWarning = !expired && validity.secondsLeft <= OTP_EXPIRY_WARNING_SECONDS;
  // Resend only unlocks once the current code has actually expired — a code that's
  // still valid shouldn't be discardable by an early resend.
  const canResend = expired;

  // Fire the expiry warning toast once, on the moment the countdown crosses the threshold.
  const warnedRef = useRef(false);
  useEffect(() => {
    if (showExpiryWarning && !warnedRef.current) {
      warnedRef.current = true;
      toastWarning("Your code is about to expire — enter it now or resend a new one.");
    }
    if (!showExpiryWarning) warnedRef.current = false;
  }, [showExpiryWarning]);

  const handleVerify = async () => {
    if (otp.length !== OTP_LENGTH || expired || verifying) return;
    setVerifying(true);
    const result = await onVerify(otp);
    setVerifying(false);
    if (result.success) {
      onVerified(otp, result.resetToken, result.wasClaim, result.creatorName);
      return;
    }
    // Our own countdown is the source of truth for expiry — if it still shows time
    // left, a backend OTP_EXPIRED really means the code was wrong, not expired.
    if (result.code === "OTP_EXPIRED" && validity.secondsLeft > 0) {
      toastError("Incorrect code. Please check and try again.");
      return;
    }
    if (result.code === "OTP_EXPIRED") validity.reset(0);
    toastError(result.error ?? "Unable to verify code.");
  };

  const handleResend = async () => {
    if (!canResend || rateLimitCountdown.secondsLeft > 0 || resending) return;
    setResending(true);
    const result = await onResend();
    setResending(false);
    if (result.success) {
      setOtp("");
      validity.reset(OTP_VALIDITY_SECONDS);
      updateAuthFlowState(email, {
        otpExpiresAt: Date.now() + OTP_VALIDITY_SECONDS * 1000,
        resendBlockedUntil: undefined,
      });
      toastSuccess("A new OTP has been sent successfully. Please check your email.");
    } else if (result.code === "TOO_MANY_ATTEMPTS" && result.retryAfterSeconds) {
      rateLimitCountdown.reset(result.retryAfterSeconds);
      updateAuthFlowState(email, { resendBlockedUntil: Date.now() + result.retryAfterSeconds * 1000 });
    } else {
      toastError(result.error ?? "Unable to resend code.");
    }
  };

  return (
    <div className="w-full max-w-sm">
      <h2 className="text-[28px] font-normal leading-tight text-[#0A2947]">{title}</h2>

      {/* Wrapped in flex+gap (same fix used for the button stack in PatientSignInPage)
          so this spacing can't collapse or get squeezed by the wrapped email line. */}
      <div className="mt-2 mb-9 flex flex-col gap-2">
        <p className="text-sm text-[#9CA3AF]">
          {subtitle} <span className="font-semibold text-[#0A2947]">{email}</span>
        </p>

        <p className="text-sm text-[#9CA3AF]">
          {expired ? (
            "Code expired."
          ) : (
            <>
              Code expires in{" "}
              <span className="font-semibold text-[#0A2947]">{formatTime(validity.secondsLeft)}</span>
            </>
          )}
        </p>
      </div>

      <div className="mb-8">
        <InputOTP
          length={OTP_LENGTH}
          value={otp}
          onChange={setOtp}
          disabled={expired || verifying}
          containerClassName="[&_.ant-otp-input:not([value=''])]:!bg-[#F36F8F] [&_.ant-otp-input:not([value=''])]:!text-white [&_.ant-otp-input:not([value=''])]:!border-[#F36F8F]"
        />
      </div>

      <AuthSubmitButton
        htmlType="button"
        onClick={handleVerify}
        loading={verifying}
        disabled={expired || otp.length !== OTP_LENGTH}
        className="!mb-6"
      >
        Verify OTP
      </AuthSubmitButton>

      <p className="text-center text-sm text-[#9CA3AF]">
        {rateLimitCountdown.secondsLeft > 0 ? (
          <>
            You've reached the maximum number of attempts.
            <br />
            Try again in{" "}
            <span className="font-semibold text-[#0A2947]">{formatTime(rateLimitCountdown.secondsLeft)}</span>
          </>
        ) : (
          <>
            Didn't get the code?{" "}
            <Button
              variant="link"
              onClick={handleResend}
              disabled={resending || !canResend}
              className="!h-auto !p-0 !font-semibold !text-[#000000] hover:!text-[#000000] hover:!underline disabled:!no-underline"
            >
              {resending ? "Resending…" : "Resend OTP"}
            </Button>
          </>
        )}
      </p>
    </div>
  );
}
